import React from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useUser } from "../contexts/UserContext";
import { API_BASE_URL, getAuthToken, getUserId } from "../services/authService";
import {
  ADMIN_APPOINTMENT_SOCKET_EVENT,
  AdminAppointmentSocketPayload,
  connectAdminAppointmentSocket,
  disconnectAdminAppointmentSocket,
  resolveAdminAppointmentSocketUrl,
  unlockNotificationSound,
} from "../services/adminAppointmentSocket";

const resolveOrganizationId = (value: any) =>
  String(
    value?.organizationId ||
      value?.organization_id ||
      value?.orgId ||
      value?.organization?.id ||
      ""
  );

const getStoredOrganizationId = () => {
  try {
    const userRaw = localStorage.getItem("user");
    const storedUser = userRaw ? JSON.parse(userRaw) : null;
    return resolveOrganizationId(storedUser);
  } catch {
    return "";
  }
};

const formatAppointmentTime = (value: any) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime()) && raw.includes("T")) {
    return parsed.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  return raw;
};

const buildAppointmentNotificationText = (
  payload: AdminAppointmentSocketPayload
) => {
  if (payload.type === "APPOINTMENT_UPDATED" && payload.message) {
    return payload.message;
  }

  const appointment = payload.appointment || {};
  const clientName = appointment.clientName || "Client";
  const unitName = appointment.unitName
    ? String(appointment.unitName)
    : appointment.unit_name
    ? String(appointment.unit_name)
    : "";
  const message = payload.message || "Appointment update";
  const startTime = String(
    appointment.time ||
      appointment.startTime ||
      appointment.start_time ||
      appointment.time_start ||
      formatAppointmentTime(appointment.start_at) ||
      ""
  );
  const endTime = String(
    appointment.endTime ||
      appointment.end_time ||
      appointment.endTimeFormatted ||
      appointment.end_time_formatted ||
      appointment.time_end ||
      formatAppointmentTime(appointment.end_at) ||
      ""
  );
  const timeRange =
    appointment.timeRange ||
    appointment.time_range ||
    appointment.timeRangeFormatted ||
    appointment.time_range_formatted ||
    (startTime && endTime ? `${startTime} - ${endTime}` : startTime);
  const unitSuffix =
    unitName && !message.toLowerCase().includes(unitName.toLowerCase())
      ? ` at ${unitName}`
      : "";
  const timeSuffix =
    timeRange && !message.toLowerCase().includes(String(timeRange).toLowerCase())
      ? ` from ${timeRange}`
      : "";

  return `${message}${unitSuffix}${timeSuffix}: ${clientName}`;
};

const showLiveAppointmentToast = (
  payload: AdminAppointmentSocketPayload,
  variant: "success" | "info"
) => {
  const options = {
    toastId: `${payload.type}-${
      payload.appointment?.id || payload.timestamp || Date.now()
    }`,
  };
  const message = buildAppointmentNotificationText(payload);

  if (variant === "success") {
    toast.success(message, options);
    return;
  }

  toast.info(message, options);
};

const AdminAppointmentSocketListener: React.FC = () => {
  const location = useLocation();
  const { user } = useUser();
  const organizationId = resolveOrganizationId(user) || getStoredOrganizationId();
  const isPublicAuthRoute = ["/login", "/signup", "/forgot-password"].includes(
    location.pathname
  );
  const isAuthenticated = Boolean(getAuthToken() && getUserId());
  const canUseLiveNotifications =
    !isPublicAuthRoute && isAuthenticated && Boolean(organizationId);

  React.useEffect(() => {
    if (!canUseLiveNotifications) return;

    const unlock = () => unlockNotificationSound();
    const events = ["pointerdown", "keydown", "touchstart"];

    events.forEach((event) => {
      window.addEventListener(event, unlock, { once: true });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, unlock);
      });
    };
  }, [canUseLiveNotifications]);

  React.useEffect(() => {
    if (!canUseLiveNotifications) {
      disconnectAdminAppointmentSocket();
      return;
    }

    const emitRefreshEvent = (payload: AdminAppointmentSocketPayload) => {
      window.dispatchEvent(
        new CustomEvent(ADMIN_APPOINTMENT_SOCKET_EVENT, { detail: payload })
      );
    };

    connectAdminAppointmentSocket({
      apiUrl: resolveAdminAppointmentSocketUrl(API_BASE_URL),
      organizationId,
      isNotificationPlaybackAllowed: () => Boolean(getAuthToken()),
      onAppointmentCreated: (payload) => {
        showLiveAppointmentToast(payload, "success");
        emitRefreshEvent(payload);
      },
      onAppointmentCancelled: (payload) => {
        showLiveAppointmentToast(payload, "info");
        emitRefreshEvent(payload);
      },
      onAppointmentUpdated: (payload) => {
        showLiveAppointmentToast(payload, "info");
        emitRefreshEvent(payload);
      },
    });

    return () => {
      disconnectAdminAppointmentSocket();
    };
  }, [canUseLiveNotifications, organizationId]);

  return null;
};

export default AdminAppointmentSocketListener;
